"use client";

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { AuthContext } from "@/app/contexts";
import { resolveHelioTraceGatewaySession } from "@/lib/helio-trace-gateway-session";
import { fetchHeliotraceSettings, updateHeliotraceSettings } from "@/shared/cors-edge-lambda-api";
import { DEFAULT_APP_LOCALE, type AppLocale } from "./locales";
import { normalizeEnabledLocales, validateEnabledLocales } from "./languageSettings";

export type EnabledLocalesValue = {
  enabledLocales: readonly AppLocale[];
  isLoading: boolean;
  loadFailed: boolean;
  refresh: () => Promise<void>;
  updateEnabledLocales: (enabledLocales: readonly AppLocale[]) => Promise<void>;
};

const EnabledLocalesContext = createContext<EnabledLocalesValue | null>(null);

export function EnabledLocalesProvider({ children }: { children: ReactNode }) {
  const auth = useContext(AuthContext);
  const [enabledLocales, setEnabledLocales] = useState<AppLocale[]>([DEFAULT_APP_LOCALE]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);

  const bearerToken = useCallback(
    () =>
      auth?.getRealAccessToken?.() ??
      auth?.realAccessToken ??
      auth?.getAccessToken?.() ??
      auth?.accessToken ??
      "",
    [auth],
  );

  const load = useCallback(
    async (signal?: AbortSignal) => {
      setIsLoading(true);
      setLoadFailed(false);
      const session = await resolveHelioTraceGatewaySession(bearerToken());
      if (signal?.aborted) return;
      if (!session) {
        setEnabledLocales([DEFAULT_APP_LOCALE]);
        setLoadFailed(true);
        setIsLoading(false);
        return;
      }

      try {
        const settings = await fetchHeliotraceSettings({
          cellId: session.cellId,
          accessToken: session.accessToken,
          signal,
        });
        if (signal?.aborted) return;
        setEnabledLocales(normalizeEnabledLocales(settings.enabledLocales));
      } catch {
        if (signal?.aborted) return;
        setEnabledLocales([DEFAULT_APP_LOCALE]);
        setLoadFailed(true);
      } finally {
        if (!signal?.aborted) setIsLoading(false);
      }
    },
    [bearerToken],
  );

  useEffect(() => {
    const controller = new AbortController();
    void load(controller.signal);
    return () => controller.abort();
  }, [load]);

  const refresh = useCallback(async () => {
    await load();
  }, [load]);

  const saveEnabledLocales = useCallback(
    async (next: readonly AppLocale[]) => {
      const validationError = validateEnabledLocales(next);
      if (validationError) {
        throw new Error(`Invalid enabled locales: ${validationError}`);
      }

      const session = await resolveHelioTraceGatewaySession(bearerToken());
      if (!session) throw new Error("No authenticated session");

      const settings = await updateHeliotraceSettings({
        cellId: session.cellId,
        accessToken: session.accessToken,
        enabledLocales: [...next],
      });
      setEnabledLocales(normalizeEnabledLocales(settings.enabledLocales));
      setLoadFailed(false);
    },
    [bearerToken],
  );

  const value = useMemo(
    () => ({
      enabledLocales,
      isLoading,
      loadFailed,
      refresh,
      updateEnabledLocales: saveEnabledLocales,
    }),
    [enabledLocales, isLoading, loadFailed, refresh, saveEnabledLocales],
  );

  return (
    <EnabledLocalesContext.Provider value={value}>{children}</EnabledLocalesContext.Provider>
  );
}

export function useOptionalEnabledLocales(): EnabledLocalesValue | null {
  return useContext(EnabledLocalesContext);
}

export function useEnabledLocales(): EnabledLocalesValue {
  const value = useOptionalEnabledLocales();
  if (!value) {
    throw new Error("useEnabledLocales must be used within EnabledLocalesProvider");
  }
  return value;
}
